'use client';

import React, { useState, useRef } from 'react';
import { Plus, X, Upload, FileText, Star, Phone, Mail, MapPin, ChevronRight, ChevronLeft, Tag, MessageSquare, User } from 'lucide-react';
import type { Applicant, JobPosting, PipelineStage } from '../page';

interface ApplicantPipelineProps {
  job: JobPosting | null;
  applicants: Applicant[];
  onUpdateApplicant: (a: Applicant) => void;
  onAddApplicant: (a: Applicant) => void;
  onBack: () => void;
}

const STAGES: { id: PipelineStage; label: string; color: string; bg: string; border: string }[] = [
  { id: 'applied', label: 'Applied', color: 'text-muted-foreground', bg: 'bg-muted/50', border: 'border-border' },
  { id: 'screening', label: 'Screening', color: 'text-info', bg: 'bg-info/5', border: 'border-info/20' },
  { id: 'interview', label: 'Interview', color: 'text-warning', bg: 'bg-warning/5', border: 'border-warning/20' },
  { id: 'offer', label: 'Offer', color: 'text-primary', bg: 'bg-primary/5', border: 'border-primary/20' },
  { id: 'hired', label: 'Hired', color: 'text-success', bg: 'bg-success/5', border: 'border-success/20' },
];

const STAGE_ORDER: PipelineStage[] = ['applied', 'screening', 'interview', 'offer', 'hired'];

function StarRating({ value, onChange }: { value: number; onChange?: (v: number) => void }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <button
          key={s}
          onClick={() => onChange?.(s)}
          className={`${onChange ? 'cursor-pointer' : 'cursor-default'}`}
          type="button"
        >
          <Star
            size={12}
            className={s <= value ? 'text-warning fill-warning' : 'text-muted-foreground/30'}
          />
        </button>
      ))}
    </div>
  );
}

function ApplicantCard({
  applicant,
  onClick,
}: {
  applicant: Applicant;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className="bg-card border border-border rounded-xl p-3.5 cursor-pointer hover:border-primary/30 hover:shadow-sm transition-all group"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-600 flex-shrink-0">
            {applicant.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
          </div>
          <div className="min-w-0">
            <p className="text-xs font-600 text-foreground truncate">{applicant.name}</p>
            <p className="text-[10px] text-muted-foreground">{applicant.role} · {applicant.experience}</p>
          </div>
        </div>
        <ChevronRight size={12} className="text-muted-foreground/40 group-hover:text-primary transition-colors flex-shrink-0 mt-1" />
      </div>
      <div className="flex items-center justify-between">
        <StarRating value={applicant.rating} />
        <div className="flex items-center gap-1">
          {applicant.resumeFileName && (
            <span className="flex items-center gap-0.5 text-[9px] text-success bg-success/10 px-1.5 py-0.5 rounded-full">
              <FileText size={9} />
              Resume
            </span>
          )}
          {applicant.tags.slice(0, 2).map((tag) => (
            <span key={tag} className="text-[9px] text-muted-foreground bg-muted px-1.5 py-0.5 rounded-full">
              {tag}
            </span>
          ))}
        </div>
      </div>
      <p className="text-[10px] text-muted-foreground mt-1.5">Applied {applicant.appliedDate}</p>
    </div>
  );
}

const emptyAddForm = {
  name: '',
  email: '',
  phone: '',
  location: '',
  experience: '',
  notes: '',
};

export default function ApplicantPipeline({
  job,
  applicants,
  onUpdateApplicant,
  onAddApplicant,
  onBack,
}: ApplicantPipelineProps) {
  const [selectedApplicant, setSelectedApplicant] = useState<Applicant | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState(emptyAddForm);
  const [addStage, setAddStage] = useState<PipelineStage>('applied');
  const [editNotes, setEditNotes] = useState('');
  const [editingNotes, setEditingNotes] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const addFileInputRef = useRef<HTMLInputElement>(null);
  const [addResumeFile, setAddResumeFile] = useState<File | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const byStage = (stage: PipelineStage) => applicants.filter((a) => a.stage === stage);

  const moveStage = (applicant: Applicant, direction: 'forward' | 'back') => {
    const idx = STAGE_ORDER.indexOf(applicant.stage);
    const nextIdx = direction === 'forward' ? idx + 1 : idx - 1;
    if (nextIdx < 0 || nextIdx >= STAGE_ORDER.length) return;
    const updated = { ...applicant, stage: STAGE_ORDER[nextIdx], lastUpdated: new Date().toISOString().split('T')[0] };
    onUpdateApplicant(updated);
    setSelectedApplicant(updated);
  };

  const handleRating = (applicant: Applicant, rating: number) => {
    const updated = { ...applicant, rating };
    onUpdateApplicant(updated);
    setSelectedApplicant(updated);
  };

  const handleSaveNotes = () => {
    if (!selectedApplicant) return;
    const updated = { ...selectedApplicant, notes: editNotes };
    onUpdateApplicant(updated);
    setSelectedApplicant(updated);
    setEditingNotes(false);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedApplicant) return;
    setUploadedFile(file);
    const updated = { ...selectedApplicant, resumeFileName: file.name, resumeUrl: null };
    onUpdateApplicant(updated);
    setSelectedApplicant(updated);
  };

  const handleAddSubmit = () => {
    if (!addForm.name.trim() || !addForm.email.trim() || !job) return;
    const newApplicant: Applicant = {
      id: `app-${Date.now()}`,
      name: addForm.name,
      role: job.title.split('–')[0]?.trim() ?? 'Staff',
      jobId: job.id,
      jobTitle: job.title,
      email: addForm.email,
      phone: addForm.phone,
      stage: addStage,
      appliedDate: new Date().toISOString().split('T')[0],
      lastUpdated: new Date().toISOString().split('T')[0],
      resumeFileName: addResumeFile?.name ?? null,
      resumeUrl: null,
      notes: addForm.notes,
      rating: 0,
      experience: addForm.experience,
      location: addForm.location,
      tags: [],
    };
    onAddApplicant(newApplicant);
    setShowAddModal(false);
    setAddForm(emptyAddForm);
    setAddResumeFile(null);
  };

  const openDetail = (applicant: Applicant) => {
    setSelectedApplicant(applicant);
    setEditNotes(applicant.notes);
    setEditingNotes(false);
    setUploadedFile(null);
  };

  const currentStageIdx = selectedApplicant ? STAGE_ORDER.indexOf(selectedApplicant.stage) : -1;

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">{applicants.length} applicants total</span>
        </div>
        <button
          onClick={() => { setShowAddModal(true); setAddStage('applied'); }}
          className="flex items-center gap-1.5 px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-500 hover:bg-primary/90 transition-colors"
        >
          <Plus size={14} />
          Add Applicant
        </button>
      </div>

      {/* Kanban Board */}
      <div className="grid grid-cols-5 gap-3 overflow-x-auto pb-2">
        {STAGES.map((stage) => {
          const cards = byStage(stage.id);
          return (
            <div key={stage.id} className={`rounded-xl border ${stage.border} ${stage.bg} p-3 min-w-[180px]`}>
              <div className="flex items-center justify-between mb-3">
                <span className={`text-xs font-600 ${stage.color}`}>{stage.label}</span>
                <span className={`text-[10px] font-700 px-1.5 py-0.5 rounded-full ${stage.bg} ${stage.color} border ${stage.border}`}>
                  {cards.length}
                </span>
              </div>
              <div className="space-y-2">
                {cards.map((applicant) => (
                  <ApplicantCard key={applicant.id} applicant={applicant} onClick={() => openDetail(applicant)} />
                ))}
                {cards.length === 0 && (
                  <div className="text-center py-6 text-muted-foreground/40">
                    <User size={20} className="mx-auto mb-1" />
                    <p className="text-[10px]">No candidates</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Applicant Detail Panel */}
      {selectedApplicant && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-end">
          <div className="bg-card border-l border-border h-full w-full max-w-md overflow-y-auto flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-border flex-shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-600">
                  {selectedApplicant.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                </div>
                <div>
                  <h2 className="text-sm font-600 text-foreground">{selectedApplicant.name}</h2>
                  <p className="text-xs text-muted-foreground">{selectedApplicant.role} · {selectedApplicant.experience}</p>
                </div>
              </div>
              <button onClick={() => setSelectedApplicant(null)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground transition-colors">
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-5">
              {/* Stage workflow */}
              <div>
                <p className="text-xs font-500 text-muted-foreground mb-2">Pipeline Stage</p>
                <div className="flex items-center gap-1 mb-3">
                  {STAGES.map((s, i) => (
                    <React.Fragment key={s.id}>
                      <div
                        className={`flex-1 text-center py-1.5 rounded-md text-[10px] font-600 border transition-all ${
                          selectedApplicant.stage === s.id
                            ? `${s.bg} ${s.color} ${s.border}`
                            : i < currentStageIdx
                            ? 'bg-success/10 text-success border-success/20' :'bg-muted/30 text-muted-foreground/50 border-transparent'
                        }`}
                      >
                        {s.label}
                      </div>
                      {i < STAGES.length - 1 && (
                        <ChevronRight size={10} className="text-muted-foreground/30 flex-shrink-0" />
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => moveStage(selectedApplicant, 'back')}
                    disabled={currentStageIdx === 0}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg border border-border bg-card text-xs font-500 text-foreground hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft size={12} />
                    Move Back
                  </button>
                  <button
                    onClick={() => moveStage(selectedApplicant, 'forward')}
                    disabled={currentStageIdx === STAGE_ORDER.length - 1}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-500 hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Advance
                    <ChevronRight size={12} />
                  </button>
                </div>
              </div>

              {/* Contact info */}
              <div>
                <p className="text-xs font-500 text-muted-foreground mb-2">Contact</p>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 text-xs text-foreground">
                    <Mail size={12} className="text-muted-foreground" />
                    <a href={`mailto:${selectedApplicant.email}`} className="hover:text-primary transition-colors">{selectedApplicant.email}</a>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-foreground">
                    <Phone size={12} className="text-muted-foreground" />
                    <span>{selectedApplicant.phone}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-foreground">
                    <MapPin size={12} className="text-muted-foreground" />
                    <span>{selectedApplicant.location}</span>
                  </div>
                </div>
              </div>

              {/* Rating */}
              <div>
                <p className="text-xs font-500 text-muted-foreground mb-2">Rating</p>
                <StarRating value={selectedApplicant.rating} onChange={(v) => handleRating(selectedApplicant, v)} />
              </div>

              {/* Tags */}
              {selectedApplicant.tags.length > 0 && (
                <div>
                  <p className="text-xs font-500 text-muted-foreground mb-2">Certifications & Tags</p>
                  <div className="flex flex-wrap gap-1.5">
                    {selectedApplicant.tags.map((tag) => (
                      <span key={tag} className="flex items-center gap-1 text-[10px] bg-muted text-muted-foreground px-2 py-1 rounded-full">
                        <Tag size={9} />
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Resume */}
              <div>
                <p className="text-xs font-500 text-muted-foreground mb-2">Resume</p>
                {selectedApplicant.resumeFileName ? (
                  <div className="flex items-center gap-2 p-3 bg-success/5 border border-success/20 rounded-lg">
                    <FileText size={16} className="text-success flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-500 text-foreground truncate">{selectedApplicant.resumeFileName}</p>
                      <p className="text-[10px] text-muted-foreground">Uploaded</p>
                    </div>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="text-[10px] text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Replace
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full flex flex-col items-center gap-2 p-4 border-2 border-dashed border-border rounded-lg hover:border-primary/40 hover:bg-primary/5 transition-all text-muted-foreground hover:text-primary"
                  >
                    <Upload size={18} />
                    <span className="text-xs font-500">Upload Resume</span>
                    <span className="text-[10px]">PDF, DOC, DOCX up to 10MB</span>
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  className="hidden"
                  onChange={handleFileUpload}
                />
              </div>

              {/* Notes */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs font-500 text-muted-foreground">Notes</p>
                  {!editingNotes && (
                    <button
                      onClick={() => { setEditNotes(selectedApplicant.notes); setEditingNotes(true); }}
                      className="text-[10px] text-primary hover:underline"
                    >
                      Edit
                    </button>
                  )}
                </div>
                {editingNotes ? (
                  <div className="space-y-2">
                    <textarea
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      rows={4}
                      placeholder="Add notes about this candidate..."
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                    />
                    <div className="flex gap-2">
                      <button onClick={() => setEditingNotes(false)} className="flex-1 py-1.5 rounded-lg border border-border text-xs font-500 text-foreground hover:bg-muted transition-colors">Cancel</button>
                      <button onClick={handleSaveNotes} className="flex-1 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-500 hover:bg-primary/90 transition-colors">Save</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-start gap-2 p-3 bg-muted/30 rounded-lg min-h-[60px]">
                    <MessageSquare size={12} className="text-muted-foreground mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-foreground whitespace-pre-wrap">
                      {selectedApplicant.notes || <span className="text-muted-foreground italic">No notes yet</span>}
                    </p>
                  </div>
                )}
              </div>

              {/* Applied info */}
              <div className="text-[10px] text-muted-foreground space-y-0.5 pt-2 border-t border-border">
                <p>Applied: {selectedApplicant.appliedDate}</p>
                <p>Last updated: {selectedApplicant.lastUpdated}</p>
                <p>Job: {selectedApplicant.jobTitle}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Applicant Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card border border-border rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-border">
              <h2 className="text-base font-600 text-foreground">Add Applicant</h2>
              <button onClick={() => setShowAddModal(false)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground transition-colors">
                <X size={16} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-500 text-muted-foreground mb-1.5">Full Name *</label>
                <input
                  type="text"
                  value={addForm.name}
                  onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="Jane Smith"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-xs font-500 text-muted-foreground mb-1.5">Email *</label>
                <input
                  type="email"
                  value={addForm.email}
                  onChange={(e) => setAddForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="jane@email.com"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-500 text-muted-foreground mb-1.5">Phone</label>
                  <input
                    type="tel"
                    value={addForm.phone}
                    onChange={(e) => setAddForm((f) => ({ ...f, phone: e.target.value }))}
                    placeholder="(312) 555-0000"
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
                <div>
                  <label className="block text-xs font-500 text-muted-foreground mb-1.5">Location</label>
                  <input
                    type="text"
                    value={addForm.location}
                    onChange={(e) => setAddForm((f) => ({ ...f, location: e.target.value }))}
                    placeholder="City, State"
                    className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-500 text-muted-foreground mb-1.5">Experience</label>
                <input
                  type="text"
                  value={addForm.experience}
                  onChange={(e) => setAddForm((f) => ({ ...f, experience: e.target.value }))}
                  placeholder="e.g. 3 years ICU"
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-xs font-500 text-muted-foreground mb-1.5">Initial Stage</label>
                <div className="flex gap-1 flex-wrap">
                  {STAGES.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setAddStage(s.id)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-500 border transition-all ${
                        addStage === s.id ? `${s.bg} ${s.color} ${s.border}` : 'bg-muted/30 text-muted-foreground border-transparent hover:border-border'
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-xs font-500 text-muted-foreground mb-1.5">Resume (optional)</label>
                <button
                  onClick={() => addFileInputRef.current?.click()}
                  className={`w-full flex items-center gap-2 p-3 border-2 border-dashed rounded-lg transition-all text-xs ${
                    addResumeFile
                      ? 'border-success/40 bg-success/5 text-success' :'border-border hover:border-primary/40 hover:bg-primary/5 text-muted-foreground hover:text-primary'
                  }`}
                >
                  <Upload size={14} />
                  {addResumeFile ? addResumeFile.name : 'Upload Resume (PDF, DOC, DOCX)'}
                </button>
                <input
                  ref={addFileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx"
                  className="hidden"
                  onChange={(e) => setAddResumeFile(e.target.files?.[0] ?? null)}
                />
              </div>
              <div>
                <label className="block text-xs font-500 text-muted-foreground mb-1.5">Notes</label>
                <textarea
                  value={addForm.notes}
                  onChange={(e) => setAddForm((f) => ({ ...f, notes: e.target.value }))}
                  rows={3}
                  placeholder="Initial notes about this candidate..."
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 p-5 border-t border-border">
              <button
                onClick={() => setShowAddModal(false)}
                className="px-4 py-2 rounded-lg border border-border bg-card text-sm font-500 text-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddSubmit}
                disabled={!addForm.name.trim() || !addForm.email.trim()}
                className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-500 hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Applicant
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
